import type {
  BookingMasterData,
  MasterParameter,
  TenantParameter,
} from 'repositories/BerthApi/getBookingMasterData/type'

import { TenantParameterTypes } from 'constants/ReceptionCustom'

export const ADDITIONAL_COLUMN_INDEXES = [
  1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23,
] as const

export const NUMBER_OF_ADDITIONAL_COLUMNS = ADDITIONAL_COLUMN_INDEXES.length

export type AdditionalColumnIndex = (typeof ADDITIONAL_COLUMN_INDEXES)[number]

/**
 * tenant_parameter の columnName（snake_case）を form values のキー（camelCase）に変換。
 * 例: "import_package_type" → "importPackageType"
 */
export const columnNameToFormKey = (columnName: string): string =>
  columnName.replace(/_([a-z0-9])/g, (_, c: string) => c.toUpperCase())

export type ImportExportSide = 'import' | 'export'

export const isAdditionalColumn = (columnName: string): boolean =>
  columnName.includes('additional_column')

/** import_/export_ プレフィックスを除いたカラム名 */
const stripSide = (columnName: string): string => columnName.replace(/^(import|export)_/, '')

/**
 * パラメータが select 型かを判定（PC版 services/Booking.js::isSelectType 相当）。
 * - TenantParameterTypes.SELECT (=2) なら true
 * - READ_ONLY (=0) でも、columnName が package_type / vehicle_category の組み込み select の場合 true
 * - その他は false（テキスト入力）
 */
export const isSelectType = (parameter: Pick<TenantParameter, 'type' | 'columnName'>): boolean => {
  if (parameter.type === TenantParameterTypes.SELECT.id) return true
  if (parameter.type === TenantParameterTypes.READ_ONLY.id) {
    const base = stripSide(parameter.columnName)
    return base === 'package_type' || base === 'vehicle_category'
  }
  return false
}

/**
 * tenant_parameters[side] を「表示対象 + display_order 順」に並べ替える。
 * display_flag が undefined のものはサーバー側で表示対象として返している前提で true 扱い。
 */
export const sortDisplayableParameters = (parameters: TenantParameter[]): TenantParameter[] =>
  parameters
    .filter((p) => p.displayFlag !== false)
    .slice()
    .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))

/**
 * select 型カラムの選択肢を組み立てる。
 * - 通常は display_flag === true の項目だけを並べる
 * - 現在値（currentValue）に該当する項目だけは display_flag === false でも残す（既存予約のロード時に消えないように）
 *
 * PC版 GoodInfoForm.js::buildSelectOptions 相当。
 */
export type SelectOption = { label: string; value: string }

/**
 * セレクト型カラムの「値」として使うプロパティ。
 * - 通常の組み込み select (package_type / vehicle_category) は parameterId (数値) を使う
 * - 追加項目 (additional_column_N) は parameterName (文字列) を使う
 *   → BE の保存値が parameter_name 文字列であり、PC版 / PhoneBooking 版とも同じ挙動
 */
const valuePropOf = (columnName: string): 'parameterId' | 'parameterName' =>
  isAdditionalColumn(columnName) ? 'parameterName' : 'parameterId'

export const buildSelectOptions = (
  columnName: string,
  bookingMaster: BookingMasterData,
  currentValue: string
): SelectOption[] => {
  const base = stripSide(columnName)
  const source: MasterParameter[] = (() => {
    if (base === 'package_type') return bookingMaster.packageTypes
    if (base === 'vehicle_category') return bookingMaster.vehicleCategories
    // 追加項目: tenant_parameters 上の columnName は `additional_column_1` / `additional_column1`
    // の両表記がありうる。BE が返す master 側のキーは camelize 後 `additionalColumn1`...`additionalColumn23`。
    const m = base.match(/^additional_column_?(\d+)$/)
    if (m) {
      const key = `additionalColumn${m[1]}`
      return (bookingMaster as unknown as Record<string, MasterParameter[] | undefined>)[key] ?? []
    }
    return []
  })()

  const valueProp = valuePropOf(columnName)
  return source
    .filter((mp) => {
      const value = String(mp[valueProp] ?? '')
      // 値が空のマスタ行 (parameterName が null / 空文字) は採用しない。
      // currentValue='' のときに String(null ?? '') === '' で偽陽性マッチして
      // 大量の空 <option> が描画される問題への対策。
      if (!value) return false
      // 表示 ON の項目は採用 / OFF でも現在値に一致する場合は残す (PC版・PhoneBooking 版と同じ挙動)
      return mp.displayFlag || (currentValue !== '' && value === currentValue)
    })
    .map((mp) => ({
      label: mp.parameterName ?? '',
      value: String(mp[valueProp] ?? ''),
    }))
}

/**
 * フラグが OFF に切り替わったときに、当該サイドの全カラムを空にするためのキー集合を返す。
 * PC版 GoodInfoForm.js::buildEmptyPackageValues 相当。
 */
export const collectSideColumnNames = (
  side: ImportExportSide,
  tenantParameters: BookingMasterData['tenantParameters']
): string[] => tenantParameters[side].map((p) => p.columnName)
